import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import FormattedMessage from '../../../../../components/TranslationWrapper';
import globalMessages from '../../../../../../config/globalMessages';
import { STATISTICS_THEME as T } from './statisticsOverlayConstants';
import { calculateFilteredErrorDurationMinutes, formatAxisTick, formatErrorDuration, formatTooltipDate, getErrorSelectionKey } from './statisticsOverlayHelpers';

const getErrorNameMessage = (name) => globalMessages[name] ?? { id: `app.globalMessages.errors.${name}`, defaultMessage: name };

export default memo(function ErrorSpectrogram({
  dataByArea,
  areaIdentifiers,
  selectedErrorKeys = new Set(),
  excludedErrorKeys = new Set(),
  defaultRows = 10,
  rowHeight = 18,
  labelWidth = 120,
  minCellPx = 3,
  xTickLabelWidth = 56
}) {
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);
  const [dataWidth, setDataWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setDataWidth(Math.max(0, entry.contentRect.width - labelWidth));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [labelWidth]);

  const maxCols = dataWidth > 0 ? Math.max(2, Math.floor(dataWidth / minCellPx)) : 200;
  const maxTicks = dataWidth > 0 ? Math.max(2, Math.floor(dataWidth / xTickLabelWidth)) : 8;

  const { rows, dates, availableRowCount } = useMemo(() => {
    const byName = new Map();
    const availableNames = new Set();
    const allDates = new Set();

    (areaIdentifiers ?? []).forEach((id) => {
      (dataByArea?.[id] ?? []).forEach((point) => {
        if (!point?.date) return;
        allDates.add(point.date);
        const dur = typeof point.durationMinutes === 'number' && point.durationMinutes > 0 ? point.durationMinutes : null;

        (Array.isArray(point.errors) ? point.errors : []).forEach((err) => {
          if (!err?.name) return;
          const mins = typeof err.minutes === 'number' ? err.minutes : null;
          if (mins !== null && mins <= 0) return;
          availableNames.add(err.name);

          const errorKey = getErrorSelectionKey(id, err.name);
          if (selectedErrorKeys.size ? !selectedErrorKeys.has(errorKey) : excludedErrorKeys.has(errorKey)) return;

          const ratio = mins !== null && dur ? Math.min(1, mins / dur) : 0.5;
          if (!byName.has(err.name)) byName.set(err.name, { cells: new Map(), total: 0 });
          const row = byName.get(err.name);
          row.cells.set(point.date, Math.max(row.cells.get(point.date) ?? 0, ratio));
          row.total += mins ?? 0;
        });
      });
    });

    if (byName.size === 0 && !selectedErrorKeys.size && !excludedErrorKeys.size) {
      (areaIdentifiers ?? []).forEach((id) => {
        (dataByArea?.[id] ?? []).forEach((point) => {
          if (!point?.date) return;
          const agg = point.aggregatedBoolSignals;
          const aggMins = typeof agg === 'number' && agg > 0 ? agg : 0;
          if (aggMins <= 0) return;
          const dur = typeof point.durationMinutes === 'number' && point.durationMinutes > 0 ? point.durationMinutes : null;
          const ratio = dur ? Math.min(1, aggMins / dur) : 0.5;
          if (!byName.has('All Errors')) byName.set('All Errors', { cells: new Map(), total: 0 });
          const row = byName.get('All Errors');
          row.cells.set(point.date, Math.max(row.cells.get(point.date) ?? 0, ratio));
          row.total += aggMins;
        });
      });
    }

    const allSorted = [...allDates].sort((a, b) => new Date(a) - new Date(b));
    const step = Math.max(1, Math.ceil(allSorted.length / maxCols));
    const sampledDates = step === 1 ? allSorted : allSorted.filter((_, i) => i % step === 0);

    const sortedRows = [...byName.entries()].map(([name, { cells, total }]) => ({ name, cells, total })).sort((a, b) => b.total - a.total);

    return { rows: sortedRows, dates: sampledDates, availableRowCount: availableNames.size || sortedRows.length };
  }, [dataByArea, areaIdentifiers, excludedErrorKeys, maxCols, selectedErrorKeys]);

  const filteredErrorDurationMinutes = useMemo(
    () => calculateFilteredErrorDurationMinutes(dataByArea, areaIdentifiers, selectedErrorKeys, excludedErrorKeys),
    [areaIdentifiers, dataByArea, excludedErrorKeys, selectedErrorKeys]
  );

  if (!availableRowCount || dates.length < 2) return null;

  const visibleRows = showAll ? rows : rows.slice(0, defaultRows);
  const rowViewportHeight = Math.min(availableRowCount, defaultRows) * rowHeight;
  const tickStep = Math.max(1, Math.ceil(dates.length / maxTicks));
  const tickIndices = dates.map((_, i) => i).filter((i) => i % tickStep === 0);

  return (
    <Box ref={containerRef} sx={{ backgroundColor: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 1.5 }}>
      <Box sx={{ px: 1.25, pt: 0.75, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ color: T.textHint, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Error Spectrogram</Typography>
          <Typography noWrap sx={{ color: T.textHint, fontSize: 10 }}>{`${rows.length} types · ${dates.length} buckets`}</Typography>
          <Typography noWrap sx={{ color: '#b91c1c', fontSize: 14, fontWeight: 800 }}>{`Sum: ${formatErrorDuration(filteredErrorDurationMinutes)}`}</Typography>
        </Box>
        {(rows.length > defaultRows || showAll) && (
          <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ fontSize: 10, py: 0, px: 0.75, minHeight: 0, color: T.textHint, textTransform: 'none' }}>
            {showAll ? 'Show less' : `+${rows.length - defaultRows} more`}
          </Button>
        )}
      </Box>

      <Box>
        <Box data-testid="error-spectrogram-rows" style={{ height: rowViewportHeight, overflowY: showAll ? 'auto' : 'hidden' }}>
          {visibleRows.map(({ name, cells, total }) => (
            <Box key={name} sx={{ display: 'flex', height: rowHeight, '&:not(:last-child)': { borderBottom: `1px solid ${T.gridLine}` } }}>
              <Box title={`${name} — ${formatErrorDuration(total)}`} sx={{ width: labelWidth, flexShrink: 0, px: 1, display: 'flex', alignItems: 'center', borderRight: `1px solid ${T.border}` }}>
                <Typography noWrap sx={{ fontSize: 10, color: T.textMuted, lineHeight: 1 }}>
                  <FormattedMessage {...getErrorNameMessage(name)} identifier={name} />
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {dates.map((date) => {
                  const ratio = cells.get(date) ?? 0;
                  return (
                    <Box
                      key={date}
                      title={ratio > 0 ? `${formatTooltipDate(date)}: ${Math.round(ratio * 100)}%` : undefined}
                      sx={{ flex: 1, backgroundColor: ratio > 0 ? `rgba(220,38,38,${(0.07 + ratio * 0.78).toFixed(2)})` : 'transparent' }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', height: 20, borderTop: `1px solid ${T.border}` }}>
          <Box sx={{ width: labelWidth, flexShrink: 0, borderRight: `1px solid ${T.border}` }} />
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {tickIndices.map((i) => {
              const pct = dates.length > 1 ? (i / (dates.length - 1)) * 100 : 0;
              return (
                <Typography
                  key={dates[i]}
                  sx={{
                    position: 'absolute',
                    left: `${pct}%`,
                    transform: 'translateX(-50%)',
                    top: '50%',
                    mt: '-0.5em',
                    fontSize: 9,
                    color: T.textHint,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    pointerEvents: 'none'
                  }}>
                  {formatAxisTick(dates[i])}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
